import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Separator } from "@/components/ui/separator";
import { Flag, CheckCircle, XCircle, Trash2, Loader2, ChevronLeft, ChevronRight, Edit, Eye, Sparkles, Wand2 } from "lucide-react";

interface ProposedFix {
  recommendedAction: "edit" | "delete" | "dismiss";
  questionText?: string;
  options?: QuestionOption[];
  correctAnswer?: string;
  explanation?: string;
}

interface EnrichedReport {
  id: string;
  questionId: string;
  userId: string;
  reason: string;
  details: string | null;
  status: string;
  adminNotes: string | null;
  proposedFix: ProposedFix | null;
  aiAnalysis: string | null;
  createdAt: string;
  resolvedAt: string | null;
  questionText: string;
  reporterName: string;
}

interface QuestionOption {
  id?: string;
  text: string;
  imageUrl?: string;
  math?: string;
}

interface FullQuestion {
  id: string;
  questionText: string;
  questionImageUrl?: string;
  questionMath?: string;
  questionType: string;
  options: QuestionOption[] | null;
  correctAnswer: string;
  explanation: string | null;
  grade: number;
  subject: string;
  topic: string;
  difficulty: string;
  status: string;
}

interface ReportsResponse {
  reports: EnrichedReport[];
  total: number;
  page: number;
  limit: number;
}

const OPTION_LETTERS = ["A", "B", "C", "D", "E"];

const REASON_LABELS: Record<string, string> = {
  incorrect_answer: "Incorrect Answer",
  unclear_question: "Unclear Question",
  wrong_topic: "Wrong Topic",
  offensive_content: "Offensive Content",
  duplicate: "Duplicate",
  other: "Other",
};

function reasonClass(reason: string) {
  switch (reason) {
    case "incorrect_answer": return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300";
    case "unclear_question": return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300";
    case "wrong_topic": return "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300";
    case "offensive_content": return "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300";
    case "duplicate": return "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300";
    default: return "bg-muted text-muted-foreground";
  }
}

const STATUS_VARIANTS: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  pending: "destructive",
  resolved: "secondary",
  dismissed: "outline",
};

function AiSuggestionBanner({
  report,
  onApplyFix,
}: {
  report: EnrichedReport;
  onApplyFix: () => void;
}) {
  if (!report.proposedFix && !report.aiAnalysis) return null;
  const fix = report.proposedFix;
  const action = fix?.recommendedAction;

  const changedFields = fix
    ? [
        fix.questionText !== undefined && "question text",
        fix.options !== undefined && "options",
        fix.correctAnswer !== undefined && "correct answer",
        fix.explanation !== undefined && "explanation",
      ].filter(Boolean)
    : [];

  return (
    <div className="rounded-lg border border-blue-200 bg-blue-50/50 dark:border-blue-800 dark:bg-blue-950/30 p-3 space-y-2">
      <div className="flex items-center gap-2 text-sm font-medium text-blue-700 dark:text-blue-300">
        <Sparkles className="h-4 w-4" />
        AI Analysis
      </div>
      {report.aiAnalysis && (
        <p className="text-sm text-muted-foreground">{report.aiAnalysis}</p>
      )}
      {action === "edit" && changedFields.length > 0 && (
        <div className="flex items-center justify-between gap-2 pt-1">
          <p className="text-xs text-muted-foreground">
            Proposed changes: {changedFields.join(", ")}
          </p>
          <Button size="sm" variant="outline" className="h-7 text-xs border-blue-300 text-blue-700 hover:bg-blue-100 dark:border-blue-700 dark:text-blue-300 dark:hover:bg-blue-900" onClick={onApplyFix}>
            <Wand2 className="h-3.5 w-3.5 mr-1.5" />
            Apply AI Fix
          </Button>
        </div>
      )}
      {action === "delete" && (
        <p className="text-xs font-medium text-destructive pt-1">
          AI recommends deleting this question.
        </p>
      )}
      {action === "dismiss" && (
        <p className="text-xs font-medium text-muted-foreground pt-1">
          AI recommends dismissing this report (no changes needed).
        </p>
      )}
    </div>
  );
}

function QuestionPreview({ question }: { question: FullQuestion }) {
  let rawOptions: any[] = question.options ?? [];
  // Handle double-stringified JSON from DB
  if (typeof rawOptions === "string") {
    try { rawOptions = JSON.parse(rawOptions); } catch { rawOptions = []; }
  }
  if (rawOptions.length === 1 && typeof rawOptions[0] === "string") {
    try { rawOptions = JSON.parse(rawOptions[0]); } catch {}
  }
  const qType = question.questionType;

  // Matching questions: options is [{prompts: {p1: "...", ...}, answers: {a1: "...", ...}}]
  // correctAnswer is JSON like {"p1":"a2","p2":"a1",...}
  const isMatching = qType === "matching" && rawOptions.length > 0 && rawOptions[0]?.prompts;

  // Standard options (multiple-choice, multi-select, true-false, order-match)
  const standardOptions: QuestionOption[] = isMatching ? [] : rawOptions;

  return (
    <div className="space-y-4 rounded-md bg-muted/50 p-4">
      <div>
        <p className="text-xs text-muted-foreground mb-1 font-medium uppercase tracking-wide">Question</p>
        <p className="text-sm leading-relaxed">{question.questionText}</p>
        {question.questionImageUrl && (
          <img src={question.questionImageUrl} alt="Question" className="mt-2 max-h-48 rounded-md object-contain border" />
        )}
      </div>

      {/* Matching questions */}
      {isMatching && (() => {
        const data = rawOptions[0];
        const prompts: Record<string, string> = data.prompts;
        const answers: Record<string, string> = data.answers;
        let correctMap: Record<string, string> = {};
        try { correctMap = typeof question.correctAnswer === "string" ? JSON.parse(question.correctAnswer) : question.correctAnswer; } catch {}
        return (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Matching Pairs</p>
            {Object.entries(prompts).map(([pKey, pText]) => {
              const aKey = correctMap[pKey];
              const aText = aKey ? answers[aKey] : "–";
              return (
                <div key={pKey} className="flex items-start gap-2 rounded-md px-3 py-2 text-sm border border-green-500/60 bg-green-50 dark:bg-green-900/20">
                  <span className="font-semibold text-green-700 dark:text-green-300 flex-shrink-0">{pText}</span>
                  <span className="text-muted-foreground flex-shrink-0">&rarr;</span>
                  <span className="text-green-800 dark:text-green-300">{aText}</span>
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* Fill-blank: just show the correct answer */}
      {qType === "fill-blank" && standardOptions.length === 0 && question.correctAnswer && (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Correct Answer</p>
          <div className="rounded-md px-3 py-2 text-sm border border-green-500/60 bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-300 font-semibold">
            {question.correctAnswer}
          </div>
        </div>
      )}

      {/* Standard options (multiple-choice, multi-select, true-false, order-match) */}
      {standardOptions.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Answer Options</p>
          {standardOptions.map((opt, i) => {
            const letter = OPTION_LETTERS[i];
            const optId = opt.id;
            const optText = opt.text;
            // Handle multi-select (comma-separated or JSON array) and single answer
            let correctIds: string[] = [];
            try {
              const parsed = JSON.parse(question.correctAnswer);
              correctIds = Array.isArray(parsed) ? parsed : [String(parsed)];
            } catch {
              correctIds = question.correctAnswer.includes(',')
                ? question.correctAnswer.split(',').map(s => s.trim())
                : [question.correctAnswer];
            }
            const isCorrect =
              (optId !== undefined && correctIds.includes(optId)) ||
              correctIds.includes(optText);
            return (
              <div
                key={optId ?? letter}
                className={`flex items-start gap-2.5 rounded-md px-3 py-2 text-sm border ${
                  isCorrect
                    ? "border-green-500/60 bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-300"
                    : "border-border bg-background"
                }`}
              >
                <span className={`font-semibold flex-shrink-0 ${isCorrect ? "text-green-600 dark:text-green-400" : "text-muted-foreground"}`}>
                  {letter}.
                </span>
                <span className="flex-1">{optText}</span>
                {isCorrect && (
                  <span className="text-xs font-medium text-green-600 dark:text-green-400 flex-shrink-0">Correct</span>
                )}
              </div>
            );
          })}
        </div>
      )}
      {question.explanation && (
        <div>
          <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-1">Explanation</p>
          <p className="text-sm text-muted-foreground">{question.explanation}</p>
        </div>
      )}
      <div className="flex gap-3 text-xs text-muted-foreground flex-wrap">
        <span>Grade {question.grade}</span>
        <span>·</span>
        <span className="capitalize">{question.subject}</span>
        <span>·</span>
        <span className="capitalize">{question.topic}</span>
        <span>·</span>
        <span className="capitalize">{question.difficulty}</span>
      </div>
    </div>
  );
}

function QuestionEditor({
  question,
  onChange,
}: {
  question: FullQuestion;
  onChange: (updates: Partial<FullQuestion>) => void;
}) {
  const options: QuestionOption[] = question.options ?? [];

  const updateOption = (index: number, text: string) => {
    const updated = options.map((o, i) => (i === index ? { ...o, text } : o));
    onChange({ options: updated });
  };

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Label className="text-xs">Question Text</Label>
        <Textarea
          value={question.questionText}
          onChange={(e) => onChange({ questionText: e.target.value })}
          rows={3}
          className="text-sm resize-none"
        />
      </div>

      {options.length > 0 && (
        <div className="space-y-2">
          <Label className="text-xs">Answer Options</Label>
          {options.map((opt, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="text-xs font-semibold text-muted-foreground w-4 flex-shrink-0">{OPTION_LETTERS[i]}.</span>
              <Input
                value={opt.text}
                onChange={(e) => updateOption(i, e.target.value)}
                className="text-sm h-8"
              />
            </div>
          ))}
        </div>
      )}

      {options.length > 0 && question.questionType === 'multi-select' && (
        <div className="space-y-1.5">
          <Label className="text-xs">Correct Answers (select all that apply)</Label>
          <div className="space-y-1">
            {options.map((opt, i) => {
              const optValue = opt.id ?? opt.text;
              const letter = OPTION_LETTERS[i];
              const preview = opt.text.length > 60 ? opt.text.slice(0, 60) + "…" : opt.text;
              // Parse current correct answers (comma-separated or JSON array)
              let correctIds: string[] = [];
              try {
                const parsed = JSON.parse(question.correctAnswer);
                correctIds = Array.isArray(parsed) ? parsed : [parsed];
              } catch {
                correctIds = question.correctAnswer.split(',').map(s => s.trim());
              }
              const isChecked = correctIds.includes(optValue);
              return (
                <label key={optValue} className={`flex items-center gap-2 p-2 rounded-md border text-sm cursor-pointer ${isChecked ? 'border-green-500 bg-green-50 dark:bg-green-900/20' : 'border-border'}`}>
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => {
                      let updated: string[];
                      if (isChecked) {
                        updated = correctIds.filter(id => id !== optValue);
                      } else {
                        updated = [...correctIds, optValue];
                      }
                      onChange({ correctAnswer: updated.join(',') });
                    }}
                    className="rounded"
                  />
                  <span className="font-semibold text-muted-foreground">{letter}.</span>
                  <span>{preview}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}

      {/* Correct Answer - type-specific input */}
      {question.questionType === 'order-match' && options.length > 0 && (
        <div className="space-y-1.5">
          <Label className="text-xs">Correct Order (drag or type comma-separated letters, e.g. B,C,A,D)</Label>
          <Input
            className="text-sm font-mono"
            value={question.correctAnswer}
            onChange={(e) => onChange({ correctAnswer: e.target.value })}
            placeholder="e.g. B,C,A,D"
          />
          <div className="text-xs text-muted-foreground space-y-0.5">
            {options.map((opt, i) => (
              <p key={i}>{OPTION_LETTERS[i]}. {opt.text.length > 80 ? opt.text.slice(0, 80) + '...' : opt.text}</p>
            ))}
          </div>
        </div>
      )}

      {question.questionType === 'matching' && (
        <div className="space-y-1.5">
          <Label className="text-xs">Correct Matching (JSON format)</Label>
          <Textarea
            className="text-sm font-mono min-h-[80px]"
            value={question.correctAnswer}
            onChange={(e) => onChange({ correctAnswer: e.target.value })}
            placeholder='{"prompt1": "answer1", "prompt2": "answer2"}'
          />
        </div>
      )}

      {question.questionType === 'fill-blank' && (
        <div className="space-y-1.5">
          <Label className="text-xs">Accepted Answers (comma-separated for multiple accepted answers)</Label>
          <Input
            className="text-sm"
            value={question.correctAnswer}
            onChange={(e) => onChange({ correctAnswer: e.target.value })}
            placeholder="e.g. hook, Hook"
          />
          <p className="text-[10px] text-muted-foreground">Case-insensitive. Separate multiple accepted answers with commas.</p>
        </div>
      )}

      {question.questionType === 'table-grid' && (
        <div className="space-y-1.5">
          <Label className="text-xs">Correct Cells (comma-separated cell indices)</Label>
          <Input
            className="text-sm font-mono"
            value={question.correctAnswer}
            onChange={(e) => onChange({ correctAnswer: e.target.value })}
            placeholder="e.g. 0,3,5"
          />
        </div>
      )}

      {options.length > 0 && !['multi-select', 'order-match', 'matching', 'fill-blank', 'table-grid'].includes(question.questionType || '') && (
        <div className="space-y-1.5">
          <Label className="text-xs">Correct Answer</Label>
          <Select value={question.correctAnswer} onValueChange={(v) => onChange({ correctAnswer: v })}>
            <SelectTrigger className="text-sm">
              <SelectValue placeholder="Select correct answer" />
            </SelectTrigger>
            <SelectContent>
              {options.map((opt, i) => {
                const optValue = opt.id ?? opt.text;
                const letter = OPTION_LETTERS[i];
                const preview = opt.text.length > 60 ? opt.text.slice(0, 60) + "…" : opt.text;
                return (
                  <SelectItem key={optValue} value={optValue}>
                    {letter}. {preview}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
      )}

      {options.length === 0 && !['fill-blank', 'matching', 'table-grid'].includes(question.questionType || '') && (
        <div className="space-y-1.5">
          <Label className="text-xs">Correct Answer</Label>
          <Input
            className="text-sm"
            value={question.correctAnswer}
            onChange={(e) => onChange({ correctAnswer: e.target.value })}
            placeholder="Enter correct answer"
          />
        </div>
      )}

      <div className="space-y-1.5">
        <Label className="text-xs">Explanation (optional)</Label>
        <Textarea
          value={question.explanation ?? ""}
          onChange={(e) => onChange({ explanation: e.target.value })}
          rows={2}
          className="text-sm resize-none"
          placeholder="Explain why the answer is correct..."
        />
      </div>
    </div>
  );
}

export default function AdminQuestionReports() {
  const { toast } = useToast();
  const [statusFilter, setStatusFilter] = useState("pending");
  const [page, setPage] = useState(1);
  const [selectedReport, setSelectedReport] = useState<EnrichedReport | null>(null);
  const [mode, setMode] = useState<"preview" | "edit">("preview");
  const [editedQuestion, setEditedQuestion] = useState<FullQuestion | null>(null);
  const [adminNotes, setAdminNotes] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const LIMIT = 20;

  const { data, isLoading } = useQuery<ReportsResponse>({
    queryKey: ["/api/admin/question-reports", statusFilter, page],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), limit: String(LIMIT) });
      if (statusFilter !== "all") params.set("status", statusFilter);
      const res = await apiRequest("GET", `/api/admin/question-reports?${params}`);
      return res.json();
    },
  });

  const { data: counts } = useQuery<{ pending: number; resolved: number; dismissed: number }>({
    queryKey: ["/api/admin/question-reports/counts"],
  });

  const { data: fullQuestion, isLoading: questionLoading } = useQuery<FullQuestion>({
    queryKey: ["/api/admin/questions", selectedReport?.questionId],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/admin/questions/${selectedReport!.questionId}`);
      return res.json();
    },
    enabled: !!selectedReport,
    staleTime: 0,
  });

  const resolveMutation = useMutation({
    mutationFn: async ({ action, questionUpdates }: { action: string; questionUpdates?: Partial<FullQuestion> }) => {
      const res = await apiRequest("POST", `/api/admin/question-reports/${selectedReport!.id}/resolve`, {
        action,
        adminNotes: adminNotes.trim() || undefined,
        questionUpdates,
      });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/question-reports"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/question-reports/counts"] });
      closeDialog();
      toast({ title: "Report resolved. Reporter has been notified." });
    },
    onError: () => {
      toast({ title: "Something went wrong", variant: "destructive" });
    },
  });

  function openDialog(report: EnrichedReport) {
    setSelectedReport(report);
    setMode("preview");
    setEditedQuestion(null);
    setAdminNotes(report.adminNotes ?? "");
    setConfirmDelete(false);
  }

  function closeDialog() {
    setSelectedReport(null);
    setMode("preview");
    setEditedQuestion(null);
    setAdminNotes("");
    setConfirmDelete(false);
  }

  function handleQuestionChange(updates: Partial<FullQuestion>) {
    setEditedQuestion((prev) => (prev ? { ...prev, ...updates } : null));
  }

  function enterEditMode() {
    if (fullQuestion) {
      setEditedQuestion({ ...fullQuestion });
      setMode("edit");
    }
  }

  function applyAiSuggestion() {
    if (!fullQuestion || !selectedReport?.proposedFix) return;
    const fix = selectedReport.proposedFix;
    const suggested: FullQuestion = {
      ...fullQuestion,
      ...(fix.questionText !== undefined && { questionText: fix.questionText }),
      ...(fix.options !== undefined && { options: fix.options }),
      ...(fix.correctAnswer !== undefined && { correctAnswer: fix.correctAnswer }),
      ...(fix.explanation !== undefined && { explanation: fix.explanation }),
    };
    setEditedQuestion(suggested);
    setMode("edit");
  }

  const totalPages = data ? Math.ceil(data.total / LIMIT) : 1;
  const activeQuestion = mode === "edit" ? editedQuestion : fullQuestion;

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Reported Questions</h1>
        <p className="text-muted-foreground text-sm mt-1">Questions flagged by students as having errors or issues.</p>
      </div>

      <div className="flex gap-2 flex-wrap">
        {(["pending", "resolved", "dismissed", "all"] as const).map((s) => (
          <Button
            key={s}
            variant={statusFilter === s ? "default" : "outline"}
            size="sm"
            onClick={() => { setStatusFilter(s); setPage(1); }}
          >
            {s.charAt(0).toUpperCase() + s.slice(1)}
            {s !== "all" && counts && counts[s as keyof typeof counts] !== undefined && (
              <Badge variant="secondary" className="ml-2 text-xs px-1.5 py-0">
                {counts[s as keyof typeof counts]}
              </Badge>
            )}
          </Button>
        ))}
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <Flag className="h-4 w-4" />
            Reports
            {data && <span className="text-muted-foreground font-normal text-sm">({data.total} total)</span>}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : !data?.reports.length ? (
            <div className="py-16 text-center">
              <Flag className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No {statusFilter !== "all" ? statusFilter : ""} reports.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Question</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Reported By</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-24"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.reports.map((report) => (
                  <TableRow key={report.id} className="cursor-pointer" onClick={() => openDialog(report)}>
                    <TableCell className="max-w-xs">
                      <p className="text-sm line-clamp-2">{report.questionText}</p>
                    </TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${reasonClass(report.reason)}`}>
                        {REASON_LABELS[report.reason] ?? report.reason}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{report.reporterName}</TableCell>
                    <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                      {new Date(report.createdAt).toLocaleDateString("en-JM", { timeZone: "America/Jamaica", day: "numeric", month: "short", year: "numeric" })}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <Badge variant={STATUS_VARIANTS[report.status] ?? "outline"} className="capitalize text-xs">
                          {report.status}
                        </Badge>
                        {report.proposedFix && (
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-blue-50 text-blue-600 border-blue-200 dark:bg-blue-950 dark:text-blue-400 dark:border-blue-800">
                            <Sparkles className="h-3 w-3 mr-0.5" />
                            AI
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openDialog(report); }}>
                        Review
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <p className="text-sm text-muted-foreground">Page {page} of {totalPages}</p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" disabled={page === totalPages} onClick={() => setPage((p) => p + 1)}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Review Dialog */}
      <Dialog open={!!selectedReport} onOpenChange={(open) => { if (!open) closeDialog(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {mode === "edit" ? <Edit className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              {mode === "edit" ? "Edit Question" : "Review Report"}
            </DialogTitle>
            {selectedReport && (
              <DialogDescription>
                Reported by {selectedReport.reporterName} ·{" "}
                {new Date(selectedReport.createdAt).toLocaleDateString("en-JM", { timeZone: "America/Jamaica", day: "numeric", month: "long", year: "numeric" })} ·{" "}
                <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${reasonClass(selectedReport.reason)}`}>
                  {REASON_LABELS[selectedReport.reason] ?? selectedReport.reason}
                </span>
              </DialogDescription>
            )}
          </DialogHeader>

          {selectedReport?.details && (
            <div className="rounded-md bg-muted px-3 py-2 text-sm text-muted-foreground">
              <span className="font-medium text-foreground">Student note: </span>
              {selectedReport.details}
            </div>
          )}

          {selectedReport && (selectedReport.proposedFix || selectedReport.aiAnalysis) && (
            <AiSuggestionBanner report={selectedReport} onApplyFix={applyAiSuggestion} />
          )}

          {questionLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : activeQuestion ? (
            mode === "preview" ? (
              <QuestionPreview question={activeQuestion as FullQuestion} />
            ) : (
              <QuestionEditor
                question={activeQuestion as FullQuestion}
                onChange={handleQuestionChange}
              />
            )
          ) : null}

          <Separator />

          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Admin Notes (sent to reporter)</Label>
            <Textarea
              placeholder="Optional note for your records..."
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              className="resize-none text-sm"
              rows={2}
            />
          </div>

          <div className="flex items-center justify-between gap-2 flex-wrap pt-1">
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                className="text-destructive"
                onClick={() => setConfirmDelete(true)}
                disabled={resolveMutation.isPending || selectedReport?.status === "resolved"}
              >
                <Trash2 className="h-4 w-4 mr-1.5" />
                Delete Question
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => resolveMutation.mutate({ action: "dismissed" })}
                disabled={resolveMutation.isPending || selectedReport?.status === "dismissed"}
              >
                <XCircle className="h-4 w-4 mr-1.5" />
                Dismiss
              </Button>
            </div>
            <div className="flex gap-2">
              {mode === "preview" ? (
                <Button variant="outline" size="sm" onClick={enterEditMode} disabled={questionLoading}>
                  <Edit className="h-4 w-4 mr-1.5" />
                  Edit Question
                </Button>
              ) : (
                <Button variant="outline" size="sm" onClick={() => setMode("preview")}>
                  <Eye className="h-4 w-4 mr-1.5" />
                  Preview
                </Button>
              )}
              <Button
                size="sm"
                onClick={() =>
                  resolveMutation.mutate({
                    action: editedQuestion ? "edited" : "resolved",
                    questionUpdates: editedQuestion ?? undefined,
                  })
                }
                disabled={resolveMutation.isPending}
              >
                {resolveMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                ) : (
                  <CheckCircle className="h-4 w-4 mr-1.5" />
                )}
                {editedQuestion ? "Save & Resolve" : "Mark Resolved"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this question?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the question from the entire platform, including all student history. This cannot be undone. The reporter will be notified.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                setConfirmDelete(false);
                resolveMutation.mutate({ action: "deleted" });
              }}
            >
              Delete Question
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
